import { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { PageHeader } from '@/components/shared/page-header';
import {
  FeedbacksTable,
  feedbackTypeLabel
} from '@/modules/feedbacks/components/feedbacks-table';
import { useDebounce } from '@/hooks/use-debounce';
import {
  useFeedbacks,
  useFeedbackReasons,
  useFeedbacksFilters,
  useFeedbacksPagination
} from '@/hooks/use-feedbacks';
import { feedbackService } from '@/services/feedback.service';
import type { FeedbacksFilters } from '@/types';

const ALL_VALUE = '__all__';

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

async function downloadExcel(filters: FeedbacksFilters) {
  const result = await feedbackService.list({
    ...filters,
    page: 1,
    pageSize: 10000
  });
  const rows = result.items.map((item) => ({
    Date: formatDate(item.createdAt),
    Brand: item.brandName ?? '',
    Agent: item.agentName ?? '',
    'Agent Email': item.agentEmail ?? '',
    Sentiment: item.sentiment ?? '',
    Reason: item.feedbackType,
    "Agent's Comment": item.feedback ?? '',
    'Customer Query': item.customerQuery ?? '',
    'AI Response': item.aiMessage ?? '',
    HITL: item.isHitl ? 'Yes' : 'No',
    Addressed: item.isAddressed ? 'Yes' : 'No',
    'Addressed Note': item.addressedNote ?? '',
    'Addressed At': item.addressedAt ? formatDate(item.addressedAt) : '',
    'Account ID': item.accountId,
    'Trace ID': item.traceId ?? '',
    'Ticket URL': item.ticketUrl ?? ''
  }));

  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Feedbacks');
  XLSX.writeFile(wb, `feedbacks-${new Date().toISOString().split('T')[0]}.xlsx`);
}

function ExportButton() {
  const [filters] = useFeedbacksFilters();
  const [isExporting, setIsExporting] = useState(false);

  async function handleExport() {
    setIsExporting(true);
    try {
      await downloadExcel(filters);
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <Button
      variant='outline'
      size='sm'
      onClick={handleExport}
      disabled={isExporting}
      className='gap-1.5'
    >
      <Download className='h-4 w-4' />
      {isExporting ? 'Exporting…' : 'Export Excel'}
    </Button>
  );
}

export default function FeedbacksPage() {
  const { data, isLoading, isFetching } = useFeedbacks();
  const [filters, setFilters] = useFeedbacksFilters();
  const [{ page, pageSize }, setPagination] = useFeedbacksPagination();
  const reasons = useFeedbackReasons();
  const [searchInput, setSearchInput] = useState(filters.search ?? '');

  const debouncedSearch = useDebounce(searchInput.trim());

  useEffect(() => {
    if (debouncedSearch !== (filters.search ?? '')) {
      setFilters({ ...filters, search: debouncedSearch || undefined });
      setPagination({ page: 1, pageSize });
    }
  }, [debouncedSearch, filters, setFilters, setPagination, pageSize]);

  const items = data?.items ?? [];
  const total = data?.pagination?.total ?? 0;
  const totalPages = data?.pagination?.totalPages ?? 1;

  function applyFilters(overrides: Partial<FeedbacksFilters>) {
    setFilters({ ...filters, ...overrides });
    setPagination({ page: 1, pageSize });
  }

  function handleReset() {
    setSearchInput('');
    setFilters({});
    setPagination({ page: 1, pageSize });
  }

  const addressedValue =
    filters.isAddressed === true
      ? 'true'
      : filters.isAddressed === false
        ? 'false'
        : ALL_VALUE;

  return (
    <div className='mx-auto w-full max-w-6xl space-y-6 p-6 lg:p-8'>
      <PageHeader
        title='Agent Feedbacks'
        description='Feedbacks submitted by agents via the extension.'
        actions={<ExportButton />}
      />

      <div className='flex flex-wrap items-center gap-2 rounded-lg border border-border bg-background px-4 py-3'>
        <Input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder='Account ID or name'
          className='h-8 w-48 text-sm'
        />

        <div className='h-5 w-px bg-border' />

        <Select
          value={filters.sentiment ?? ALL_VALUE}
          onValueChange={(v) =>
            applyFilters({
              sentiment:
                v === ALL_VALUE ? undefined : (v as 'positive' | 'negative')
            })
          }
        >
          <SelectTrigger className='h-8 w-36 text-sm'>
            <SelectValue placeholder='Sentiment' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All sentiments</SelectItem>
            <SelectItem value='positive'>Positive</SelectItem>
            <SelectItem value='negative'>Negative</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={filters.feedbackType ?? ALL_VALUE}
          onValueChange={(v) =>
            applyFilters({ feedbackType: v === ALL_VALUE ? undefined : v })
          }
        >
          <SelectTrigger className='h-8 w-44 text-sm'>
            <SelectValue placeholder='Reason' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All reasons</SelectItem>
            {reasons.map((reason) => (
              <SelectItem key={reason} value={reason}>
                {feedbackTypeLabel(reason)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={addressedValue}
          onValueChange={(v) =>
            applyFilters({
              isAddressed: v === ALL_VALUE ? undefined : v === 'true'
            })
          }
        >
          <SelectTrigger className='h-8 w-40 text-sm'>
            <SelectValue placeholder='Status' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All statuses</SelectItem>
            <SelectItem value='false'>Not addressed</SelectItem>
            <SelectItem value='true'>Addressed</SelectItem>
          </SelectContent>
        </Select>

        <div className='h-5 w-px bg-border' />

        <DateRangePicker
          inline
          value={
            filters.fromDate || filters.toDate
              ? {
                  startDate: filters.fromDate
                    ? new Date(filters.fromDate)
                    : undefined,
                  endDate: filters.toDate
                    ? new Date(filters.toDate)
                    : undefined
                }
              : undefined
          }
          onChange={(range) =>
            applyFilters({
              fromDate: range.startDate
                ? range.startDate.toISOString().split('T')[0]
                : undefined,
              toDate: range.endDate
                ? range.endDate.toISOString().split('T')[0]
                : undefined
            })
          }
          placeholder={{ start: 'From', end: 'To' }}
        />

        <div className='h-5 w-px bg-border' />

        <Button
          variant='ghost'
          size='sm'
          onClick={handleReset}
          className='h-8 text-muted-foreground'
        >
          Reset
        </Button>
      </div>

      <FeedbacksTable
        items={items}
        total={total}
        isLoading={isLoading || isFetching}
        page={page}
        totalPages={totalPages}
        onPageChange={(p) => setPagination({ page: p, pageSize })}
      />
    </div>
  );
}
